import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useAuthHeader, useAuthUser } from 'react-auth-kit';
import { LayoutTemplate, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import User from '../../Models/User';
import datas from "../../Assets/data.json";
import env from 'react-dotenv';
import { Crypter } from '../../Functions/Security/Crypter';
import {
    Badge, Button, DataTable, IconButton, InfoBar, Modal, PageHeader,
    Select, Spin, TableStatusBar, useAlert
} from '../ui';
import { DataTableColumn } from '../ui/DataTable';
import FormLayoutEditor from './FormLayoutEditor';

const SCOPE_COLOR: Record<string, 'violet' | 'blue' | 'gray'> = {
    FORM: 'violet',
    FILIALE: 'blue',
    ABONNE: 'gray',
};

const SCOPE_LABEL: Record<string, string> = {
    FORM: 'Formulaire',
    FILIALE: 'Filiale',
    ABONNE: 'Abonné',
};

/** Indicateur actif/inactif d'une zone. */
function ZoneBadge({ actif, label }: { actif: any; label: string }) {
    const on = actif !== false && actif !== 0;
    return <Badge color={on ? 'green' : 'gray'}>{label} {on ? 'ON' : 'OFF'}</Badge>;
}

/**
 * Gestion des layouts d'impression (en-têtes / pieds de page PDF).
 * Consomme ManageLayout : LIST (GET), GET portée exacte + RESOLVE (éditeur),
 * SET (POST via l'éditeur), DELETE (POST).
 */
export default function GestionLayouts() {

    // USER TOKEN
    const authHeader = useAuthHeader()

    // USER SESSION
    const auth = useAuthUser()
    const user: User = JSON.parse(JSON.stringify(auth() || {}))
    const alert = useAlert()

    const [loading, setLoading] = useState(false)
    const [dataTri, setDataTri] = useState<any[]>([])
    const [selected, setSelected] = useState<any>()

    // Référentiels (portées + résolveur)
    const [filiales, setFiliales] = useState<any[]>([])
    const [formulaires, setFormulaires] = useState<any[]>([])

    // Éditeur montage
    const [openEditor, setOpenEditor] = useState(false)
    const [layoutEdit, setLayoutEdit] = useState<any>()

    // Suppression
    const [openDelete, setOpenDelete] = useState(false)
    const [toDelete, setToDelete] = useState<any>(null)
    const [deleting, setDeleting] = useState(false)

    // Résolveur de layout effectif (RESOLVE)
    const [resForm, setResForm] = useState('')
    const [resFiliale, setResFiliale] = useState('')
    const [resLoading, setResLoading] = useState(false)
    const [resScope, setResScope] = useState<string | null>(null)

    useEffect(() => {
        LoadData()
        LoadFiliales()
        LoadFormulaires()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /** POST chiffré générique (mutations : DELETE ; SET passe par l'éditeur). */
    function postApi(url: string, payload: any) {
        return axios.request({
            method: 'post',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + url,
            headers: { 'Content-Type': 'application/json', 'Authorization': authHeader() },
            data: Crypter.encrypt(JSON.stringify(payload)),
        }).then((r: any) => JSON.parse(Crypter.decrypt(r.data)).Value)
    }

    /** GET ManageLayout?CipherLayout= — lecture seule (LIST / GET / RESOLVE). */
    function getLayout(payload: any) {
        return axios.request({
            method: 'get',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.Formulaires.ManageLayoutGet + Crypter.encrypt(JSON.stringify(payload)),
            headers: { 'Accept': 'text/plain', 'Authorization': authHeader() },
        }).then((r: any) => JSON.parse(Crypter.decrypt(r.data)).Value)
    }

    function LoadData() {
        setLoading(true)
        getLayout({ Option: 'LIST', CodeAbonne: user.CodeAbonne })
            .then((res: any) => {
                setLoading(false)
                if (res?.StatutCode == 200 || res?.StatutCode === undefined) {
                    const list = Array.isArray(res.Result) ? res.Result
                        : Array.isArray(res.Result?.Layouts) ? res.Result.Layouts
                        : []
                    // LIST peut renvoyer une ligne par champ de layout — dédupliquer : 1 ligne = 1 layout
                    const seen = new Set<any>()
                    const layouts = list.filter((g: any) => {
                        const k = g.LayoutId ?? g.Id ?? `${g.Scope}|${g.CodeForm}|${g.CodeFiliale}`
                        if (seen.has(k)) return false
                        seen.add(k)
                        return true
                    })
                    setDataTri(layouts.map((g: any) => ({ ...g, id: g.LayoutId ?? g.Id })))
                } else {
                    setDataTri([])
                    alert.warning(res?.Message)
                }
            })
            .catch((error: any) => {
                setLoading(false)
                alert.error('Error', error?.message || error)
            });
    }

    function LoadFiliales() {
        axios.request({
            method: 'get',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.GetAllFiliales + Crypter.encrypt(user.CodeAbonne),
            headers: { 'Accept': 'text/plain', 'Authorization': authHeader() },
        })
            .then((response: any) => {
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200) setFiliales(res.Result || [])
            })
            .catch((error: any) => console.log(error));
    }

    /** LIST des formulaires — utilisé pour la portée FORM et le résolveur. */
    function LoadFormulaires() {
        postApi(datas.API_LINKs.Formulaires.ManageFormulaire, {
            Option: 'LIST',
            CodeAbonne: user.CodeAbonne
        })
            .then((res: any) => {
                if (res?.StatutCode == 200) {
                    setFormulaires(Array.isArray(res.Result) ? res.Result : [])
                }
            })
            .catch((error: any) => console.log(error));
    }

    /** RESOLVE — layout effectif d'un formulaire (héritage FORM > FILIALE > ABONNE). */
    function Resolve() {
        if (!resForm) { alert.warning('Sélectionnez un formulaire'); return; }
        setResLoading(true)
        getLayout({
            Option: 'RESOLVE',
            CodeAbonne: user.CodeAbonne,
            CodeForm: resForm,
            CodeFiliale: resFiliale || undefined,
        })
            .then((res: any) => {
                setResLoading(false)
                const src = res?.Result?.Layout ?? res?.Result
                setResScope(src?.Scope || src?.LayoutScope || null)
            })
            .catch((error: any) => {
                setResLoading(false)
                setResScope(null)
                alert.warning('Aucun layout résolu — héritage par défaut')
                console.log(error);
            });
    }

    function handleRowClick(row: any) {
        setSelected(dataTri.find((r: any) => r.id == row.id))
    }

    function handleEdit(row: any) {
        setLayoutEdit(dataTri.find((r: any) => r.id == row.id))
        setOpenEditor(true)
    }

    function confirmDelete() {
        if (!toDelete) return
        setDeleting(true)
        postApi(datas.API_LINKs.Formulaires.ManageLayout, {
            Option: 'DELETE',
            LayoutId: toDelete.LayoutId ?? toDelete.Id,
            CodeAbonne: user.CodeAbonne,
        })
            .then((res: any) => {
                setDeleting(false)
                if (res?.StatutCode == 200 || res?.StatutCode === undefined) {
                    alert.success(res?.Message || 'Layout désactivé')
                    setOpenDelete(false)
                    setToDelete(null)
                    LoadData()
                } else {
                    alert.warning(res?.Message || 'Suppression refusée')
                }
            })
            .catch((error: any) => {
                setDeleting(false)
                alert.error('Error', error?.message || error)
            })
    }

    const columns: DataTableColumn[] = [
        { field: 'Id', headerName: 'ID', width: 60, renderCell: ({ row }) => <span>{row.LayoutId ?? row.Id}</span> },
        {
            field: 'Scope', headerName: 'Portée', width: 110,
            renderCell: ({ value }) => <Badge color={SCOPE_COLOR[value] || 'gray'}>{SCOPE_LABEL[value] || value}</Badge>,
        },
        {
            field: 'CodeForm', headerName: 'Formulaire', width: 170,
            renderCell: ({ row }) => <span>{row.LibelleForm || row.CodeForm || '—'}</span>,
        },
        {
            field: 'CodeFiliale', headerName: 'Filiale', width: 160,
            renderCell: ({ row }) => <span>{row.LibelleFiliale || row.CodeFiliale || '—'}</span>,
        },
        {
            field: 'HeaderActif', headerName: 'En-tête', width: 100,
            renderCell: ({ value }) => <ZoneBadge actif={value} label="Haut" />,
        },
        {
            field: 'FooterActif', headerName: 'Pied', width: 100,
            renderCell: ({ value }) => <ZoneBadge actif={value} label="Bas" />,
        },
        {
            field: 'Actions', headerName: 'Actions', width: 110, sortable: false,
            renderCell: ({ row }) => (
                <div className="flex items-center gap-1">
                    <IconButton tooltip="Éditer la mise en page" color="primary" size={30}
                        onClick={(e) => { e.stopPropagation(); handleEdit(row) }}>
                        <Pencil size={15} />
                    </IconButton>
                    <IconButton tooltip="Désactiver" color="danger" size={30}
                        onClick={(e) => { e.stopPropagation(); setToDelete(row); setOpenDelete(true) }}>
                        <Trash2 size={15} />
                    </IconButton>
                </div>
            ),
        },
    ];

    return (
        <Spin spinning={loading}>
            <PageHeader
                title="Mises en page d'impression"
                subtitle="En-têtes et pieds de page PDF des formulaires — héritage FORM > FILIALE > ABONNE"
                actions={
                    <Button variant='primary' startIcon={<Plus size={16} />} onClick={() => { setLayoutEdit(undefined); setOpenEditor(true) }}>
                        Nouveau layout
                    </Button>
                }
            />

            {/* Résolveur de layout effectif + rappel de l'héritage */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 mb-3">
                <div className="eb-card flex items-start gap-3 p-3">
                    <LayoutTemplate size={18} className="mt-0.5 flex-shrink-0 text-[var(--eb-primary)]" />
                    <p className="text-xs text-[var(--eb-text-muted)] leading-relaxed">
                        La portée la plus spécifique gagne : <strong>FORM &gt; FILIALE &gt; ABONNE</strong>.
                        Un formulaire sans layout propre hérite de la filiale, puis de l'abonné.
                    </p>
                </div>
                <div className="eb-card p-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-[var(--eb-text-muted)] mb-2">
                        Layout effectif — résolution
                    </div>
                    <div className="flex flex-wrap items-end gap-2">
                        <div className="w-52">
                            <Select
                                label="Formulaire"
                                value={resForm}
                                onChange={setResForm}
                                options={[
                                    { value: '', label: '—' },
                                    ...formulaires.map((f: any) => ({ value: f.CodeForm, label: `${f.CodeForm} - ${f.Libelle || ''}` })),
                                ]}
                            />
                        </div>
                        <div className="w-44">
                            <Select
                                label="Filiale"
                                value={resFiliale}
                                onChange={setResFiliale}
                                options={[
                                    { value: '', label: 'Toutes' },
                                    ...filiales.map((f: any) => ({ value: f.CodeFiliale, label: f.CodeFiliale })),
                                ]}
                            />
                        </div>
                        <Button variant='secondary' size='sm' onClick={Resolve} loading={resLoading} startIcon={<Search size={15} />}>
                            Résoudre
                        </Button>
                        {resScope && <Badge color={SCOPE_COLOR[resScope] || 'gray'}>{SCOPE_LABEL[resScope] || resScope}</Badge>}
                    </div>
                </div>
            </div>

            <div className="eb-table-wrap">
                <DataTable
                    onRowClick={handleRowClick}
                    rows={dataTri}
                    height={300}
                    columns={columns}
                    searchable
                    emptyText="Aucun layout défini"
                />
            </div>
            <TableStatusBar count={dataTri.length} loading={loading} onRefresh={LoadData} />
            <InfoBar data={[
                { label: 'ID', value: selected?.LayoutId ?? selected?.Id },
                { label: 'Portée', value: SCOPE_LABEL[selected?.Scope] || selected?.Scope },
                { label: 'Formulaire', value: selected?.LibelleForm || selected?.CodeForm || '—' },
                { label: 'Filiale', value: selected?.LibelleFiliale || selected?.CodeFiliale || '—' },
            ]} />

            {/* Éditeur montage — portée libre (FORM / FILIALE / ABONNE) */}
            <FormLayoutEditor
                open={openEditor}
                onClose={() => { setOpenEditor(false); setLayoutEdit(undefined) }}
                layout={layoutEdit}
                filiales={filiales}
                formulaires={formulaires}
                onSaved={LoadData}
            />

            <Modal
                open={openDelete}
                onClose={() => { setOpenDelete(false); setToDelete(null) }}
                title="Désactiver le layout"
                footer={
                    <>
                        <Button variant="outline" onClick={() => { setOpenDelete(false); setToDelete(null) }}>Annuler</Button>
                        <Button variant="danger" loading={deleting} onClick={confirmDelete} startIcon={<Trash2 size={15} />}>Désactiver</Button>
                    </>
                }
            >
                <p className="text-sm text-[var(--eb-text)]">
                    Désactiver le layout <strong>{SCOPE_LABEL[toDelete?.Scope] || ''}</strong>
                    {toDelete?.CodeForm ? ` du formulaire ${toDelete.CodeForm}` : ''}
                    {toDelete?.CodeFiliale ? ` de la filiale ${toDelete.CodeFiliale}` : ''} ?
                    Les formulaires concernés retomberont sur l'héritage.
                </p>
            </Modal>
        </Spin>
    );
}
